import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	ClassicEditor,
	Essentials,
	Heading,
	Bold, Italic, Underline, Strikethrough,
	FontColor, FontBackgroundColor,
	List,
	Link,
	Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload,
	BlockQuote,
	Paragraph,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { showConfirm, showDeleteConfirm, showError, showSuccess } from '../../../utils/sweetAlertConfig';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { API_CONFIG, ICON_ACTION, parseDatetimeLocal } from '../../../contexts/constants';
import { formatDateTimeTable } from '../../../utils/dateFormatter';
import { CustomUploadAdapterPlugin } from '../../../utils/CKEditorUploadAdapter';

const Manage = ({ onLogout, userRole }) => {
	const [newsPromotions, setNewsPromotions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [showModal, setShowModal] = useState(false);
	const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
	const [showSearchForm, setShowSearchForm] = useState(false);
	const [searchFilters, setSearchFilters] = useState({
		title: '',
		types: {
			news: true,
			promotion: true
		}
	});
	const [appliedFilters, setAppliedFilters] = useState({
		title: '',
		types: {
			news: true,
			promotion: true
		}
	});
	const [formData, setFormData] = useState({
		type: 'news',
		title: '',
		content: '',
		date: '',
		priority: 'medium',
		badge: '',
		fullDetail: {
			conditions: [''],
			howToJoin: [''],
			rewards: ''
		}
	});

	// Lock body scroll when modal is open
	useBodyScrollLock(showModal);

	useEffect(() => {
		fetchNewsPromotions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, pageSize, appliedFilters]);

	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const fetchNewsPromotions = async () => {
		try {
			setLoading(true);

			// Build query parameters
			const params = new URLSearchParams({
				page: currentPage,
				pageSize: pageSize
			});

			if (appliedFilters.title) {
				params.append('title', appliedFilters.title);
			}

			// Handle type filters
			const selectedTypes = Object.keys(appliedFilters.types).filter(type => appliedFilters.types[type]);
			if (selectedTypes.length === 1) {
				// If only one type selected, use it
				params.append('type', selectedTypes[0]);
			}
			// If both or none selected, don't add type param (fetch all)

			const response = await axios.get(`${API_CONFIG.BASE_URL}/news?${params.toString()}`, {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (response.data && Array.isArray(response.data.data)) {
				setNewsPromotions(response.data.data);
				if (response.data.pagination) {
					setTotalItems(response.data.pagination.total);
				}
			} else {
				console.error('Invalid news data format');
				setNewsPromotions([]);
				setTotalItems(0);
			}
		} catch (error) {
			console.error('Error fetching news promotions:', error);
			handleApiError(error);
			setNewsPromotions([]);
			setTotalItems(0);
		} finally {
			setLoading(false);
		}
	};

	const handleApiError = (error) => {
		let errorMessage = 'ไม่สามารถโหลดข้อมูลได้';

		if (error.response) {
			errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

			if (error.response.status === 401 || error.response.status === 403) {
				errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
				showError('เซสชันหมดอายุ', errorMessage);
				setTimeout(() => {
					localStorage.removeItem('token');
					onLogout();
				}, 1500);
				return;
			}
		} else if (error.request) {
			errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
		}

		showError('เกิดข้อผิดพลาด', errorMessage);
	};

	const handleAdd = () => {
		setModalMode('add');
		setFormData({
			type: 'news',
			title: '',
			content: '',
			date: null,
			priority: 'medium',
			badge: '',
			fullDetail: {
				conditions: [''],
				howToJoin: [''],
				rewards: ''
			}
		});
		setShowModal(true);
	};

	const handleEdit = (news) => {
		setModalMode('edit');
		setFormData({
			id: news.id,
			type: news.type || 'news',
			title: news.title || '',
			content: news.content || '',
			date: news.date || '',
			priority: news.priority || 'medium',
			badge: news.badge || '',
			fullDetail: {
				conditions: news.fullDetail?.conditions || [''],
				howToJoin: news.fullDetail?.howToJoin || [''],
				rewards: news.fullDetail?.rewards || ''
			}
		});
		setShowModal(true);
	};

	const handleDelete = async (news) => {
		const result = await showDeleteConfirm({
			title: 'ยืนยันการลบ?',
			text: `คุณต้องการลบ "${news.title}" หรือไม่?`
		});

		if (result.isConfirmed) {
			try {
				await axios.delete(`${API_CONFIG.BASE_URL}/news/${news.id}`, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});

				showSuccess('สำเร็จ', 'ลบข้อมูลเรียบร้อยแล้ว');
				fetchNewsPromotions();
			} catch (error) {
				console.error('Error deleting news:', error);
				showError('เกิดข้อผิดพลาด', 'ไม่สามารถลบข้อมูลได้');
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate
		if (!formData.title.trim()) {
			showError('กรุณากรอกข้อมูล', 'กรุณากรอกหัวข้อ');
			return;
		}

		if (!formData.content.trim() || formData.content === '<p>&nbsp;</p>' || formData.content === '<p></p>') {
			showError('กรุณากรอกข้อมูล', 'กรุณากรอกเนื้อหา');
			return;
		}

		// ยืนยันก่อนบันทึก
		const confirmTitle = modalMode === 'add' ? 'ยืนยันการเพิ่มข้อมูล?' : 'ยืนยันการแก้ไข?';
		const confirmText = modalMode === 'add'
			? 'คุณต้องการเพิ่มข่าว/โปรโมชั่นนี้หรือไม่?'
			: 'คุณต้องการบันทึกการแก้ไขหรือไม่?';

		const result = await showConfirm({
			title: confirmTitle,
			text: confirmText,
			confirmText: 'บันทึก',
			cancelText: 'ยกเลิก'
		});

		if (!result.isConfirmed) {
			return;
		}

		try {
			if (modalMode === 'add') {
				await axios.post(`${API_CONFIG.BASE_URL}/news`, formData, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});
				showSuccess('สำเร็จ', 'เพิ่มข้อมูลเรียบร้อยแล้ว');
			} else {
				await axios.put(`${API_CONFIG.BASE_URL}/news/${formData.id}`, formData, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});
				showSuccess('สำเร็จ', 'แก้ไขข้อมูลเรียบร้อยแล้ว');
			}

			setShowModal(false);
			fetchNewsPromotions();
		} catch (error) {
			console.error('Error saving news:', error);
			showError('เกิดข้อผิดพลาด', 'ไม่สามารถบันทึกข้อมูลได้');
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleDateChange = (date) => {
		setFormData(prev => ({
			...prev,
			date: date
		}));
	};

	const handleContentChange = (event, editor) => {
		const data = editor.getData();
		setFormData(prev => ({
			...prev,
			content: data
		}));
	};

	const handleArrayFieldChange = (field, index, value) => {
		setFormData(prev => ({
			...prev,
			fullDetail: {
				...prev.fullDetail,
				[field]: prev.fullDetail[field].map((item, i) => i === index ? value : item)
			}
		}));
	};

	const addArrayField = (field) => {
		setFormData(prev => ({
			...prev,
			fullDetail: {
				...prev.fullDetail,
				[field]: [...prev.fullDetail[field], '']
			}
		}));
	};

	const removeArrayField = (field, index) => {
		setFormData(prev => ({
			...prev,
			fullDetail: {
				...prev.fullDetail,
				[field]: prev.fullDetail[field].filter((_, i) => i !== index)
			}
		}));
	};

	const columns = [
		{
			key: 'actions',
			header: 'จัดการ',
      		className: 'text-center',
			width: 80,
			render: (row) => (
				<div className="flex gap-2 justify-center">
					<button
						onClick={() => handleEdit(row)}
						className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
						title="แก้ไข"
					>
						{ICON_ACTION.EDIT}
					</button>
					<button
						onClick={() => handleDelete(row)}
						className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
						title="ลบ"
					>
						{ICON_ACTION.DELETE}
					</button>
				</div>
			)
		},
		{
			key: 'type',
			header: 'ประเภท',
			width: 120,
			render: (row) => (
				<span className={`flex gap-2 justify-center px-2 py-1 rounded-full text-xs font-medium ${
					row.type === 'promotion'
						? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
						: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
				}`}>
					{row.type === 'promotion' ? 'โปรโมชั่น' : 'ข่าวสาร'}
				</span>
			)
		},
		{
			key: 'title',
			header: 'หัวข้อ',
			render: (row) => (
				<div className="max-w-md truncate" title={row.title}>
					{row.title}
				</div>
			)
		},
		{
			key: 'priority',
			header: 'ความสำคัญ',
			width: 120,
			render: (row) => (
				<span className={`flex gap-2 justify-center px-2 py-1 rounded-full text-xs font-medium ${
					row.priority === 'high'
						? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
						: row.priority === 'medium'
						? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
						: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
				}`}>
					{row.priority === 'high' ? 'สูง' : row.priority === 'medium' ? 'กลาง' : 'ต่ำ'}
				</span>
			)
		},
		{
			key: 'date',
			header: 'วันที่เผยแพร่',
			className: 'text-center',
			width: '170px',
			render: (row) => (
				<div className="flex justify-center">
					{formatDateTimeTable(row.date)}
				</div>
			)
		},
	];

	const toggleSearchForm = () => {
		setShowSearchForm(!showSearchForm);
	};

	const handleSearch = () => {
		setAppliedFilters({ ...searchFilters });
		setCurrentPage(1); // Reset to first page when searching
	};
	const handleClearSearch = () => {
		const emptyFilters = {
			title: '',
			types: {
				news: true,
				promotion: true
			}
		};
		setSearchFilters(emptyFilters);
		setAppliedFilters(emptyFilters);
		setCurrentPage(1);
	};

	return (
		<Layout title="จัดการข่าวสารและโปรโมชั่น" onLogout={onLogout} userRole={userRole}>
			{/* Header Actions */}
			<div className="mb-4 flex justify-between items-center">
				<div>
					<p className="text-gray-600 dark:text-gray-400">
						จัดการข่าวสารและโปรโมชั่นทั้งหมด
					</p>
				</div>
				<div className="flex items-center gap-3">
					<button
						onClick={toggleSearchForm}
						className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
							showSearchForm
							? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
							: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
						}`}
					>
						{showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
						<span className='hidden md:inline'>ค้นหา</span>
					</button>
					<button
						onClick={handleAdd}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
					>
						{ICON_ACTION.ADD}
						<span className='hidden md:inline'>เพิ่มข่าว/โปรโมชั่น</span>
					</button>
				</div>
			</div>

			{/* Search Filter Form - Show/Hide */}
			{showSearchForm && (
				<div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 mb-4 animate-fade-in">
					<div className="space-y-4 mb-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
								หัวข้อข่าว/โปรโมชั่น
							</label>
							<input
								type="text"
								value={searchFilters.title}
								onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
								placeholder="ระบุชื่อหัวข้อข่าว/โปรโมชั่น"
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
								ประเภท
							</label>
							<div className="flex items-center gap-4">
								<label className="flex items-center space-x-2 cursor-pointer">
									<input
										type="checkbox"
										checked={searchFilters.types.news}
										onChange={(e) => setSearchFilters({
											...searchFilters,
											types: { ...searchFilters.types, news: e.target.checked }
										})}
										className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
									/>
									<span className="text-sm text-gray-700 dark:text-gray-300">ข่าวสาร</span>
								</label>
								<label className="flex items-center space-x-2 cursor-pointer">
									<input
										type="checkbox"
										checked={searchFilters.types.promotion}
										onChange={(e) => setSearchFilters({
											...searchFilters,
											types: { ...searchFilters.types, promotion: e.target.checked }
										})}
										className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
									/>
									<span className="text-sm text-gray-700 dark:text-gray-300">โปรโมชั่น</span>
								</label>
							</div>
						</div>
					</div>
					<div className="flex justify-end gap-2">
						<button
							onClick={handleSearch}
							className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
						>
							{ICON_ACTION.SEARCH}
							ค้นหา
						</button>
						<button
							onClick={handleClearSearch}
							className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
						>
							{ICON_ACTION.CLEAR}
							ล้าง
						</button>
					</div>
				</div>
			)}

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={newsPromotions}
				loading={loading}
				page={currentPage}
				pageSize={pageSize}
				totalItems={totalItems}
				onPageChange={setCurrentPage}
				onPageSizeChange={setPageSize}
				emptyMessage="ไม่พบข้อมูลข่าวสาร"
			/>

			{/* Modal */}
			{showModal && (
				<div className="fixed top-0 inset-x-0 bg-black/50 flex items-top justify-center z-50 p-4 max-h-screen h-full overflow-y-auto flex align-middle">
					<div className="flex align-middle h-auto my-auto">
						<div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl h-auto w-full max-w-full sm:max-w-lg min-w-full sm:min-w-[800px] my-5">
							<div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900 dark:text-white">
									{modalMode === 'add' ? 'เพิ่มข่าว/โปรโมชั่น' : 'แก้ไขข่าว/โปรโมชั่น'}
								</h3>
								<button
									onClick={() => setShowModal(false)}
									className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
								>
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSubmit} className="p-6 space-y-4">
								{/* Type */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ประเภท <span className="text-red-500">*</span>
									</label>
									<select
										name="type"
										value={formData.type}
										onChange={handleInputChange}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										required
									>
										<option value="news">ข่าวสาร</option>
										<option value="promotion">โปรโมชั่น</option>
									</select>
								</div>

								{/* Title */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										หัวข้อ <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										name="title"
										value={formData.title}
										onChange={handleInputChange}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										required
									/>
								</div>

								{/* Content */}
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										เนื้อหา <span className="text-red-500">*</span>
									</label>
									<div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
										<CKEditor
											editor={ClassicEditor}
											data={formData.content}
											onChange={handleContentChange}
											onReady={(editor) => {
												console.log('CKEditor is ready!', editor);
											}}
											config={{
												plugins: [
													Essentials, Heading,
													Bold, Italic, Underline, Strikethrough,
													FontColor, FontBackgroundColor,
													List, Link,
													Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload,
													BlockQuote, Paragraph,
												],
												licenseKey: 'GPL',
											extraPlugins: [CustomUploadAdapterPlugin],
												toolbar: [
													'heading', '|',
													'bold', 'italic', 'underline', 'strikethrough', '|',
													'fontColor', 'fontBackgroundColor', '|',
													'bulletedList', 'numberedList', '|',
													'link', 'imageUpload', '|',
													'blockQuote', '|',
													'undo', 'redo'
												],
												placeholder: 'ระบุเนื้อหา...',
												image: {
													toolbar: [
														'imageStyle:inline',
														'imageStyle:block',
														'imageStyle:side',
														'|',
														'toggleImageCaption',
														'imageTextAlternative'
													]
												}
											}}
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									{/* Date */}
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											วันที่เผยแพร่
										</label>
										<DatePicker
												selected={formData.date && formData.date !== '' ? (isNaN(parseDatetimeLocal(formData.date).getTime()) ? null : parseDatetimeLocal(formData.date)) : null}
											onChange={handleDateChange}
											showTimeSelect
											timeFormat="HH:mm"
											timeIntervals={15}
											dateFormat="dd/MM/yyyy HH:mm"
											placeholderText="เลือกวันที่และเวลา"
											className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											wrapperClassName="w-full"
											isClearable
										/>
									</div>

									{/* Priority */}
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											ความสำคัญ
										</label>
										<select
											name="priority"
											value={formData.priority}
											onChange={handleInputChange}
											className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										>
											<option value="low">ต่ำ</option>
											<option value="medium">กลาง</option>
											<option value="high">สูง</option>
										</select>
									</div>

									{/* Badge */}
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											ป้าย
										</label>
										<input
											type="text"
											name="badge"
											value={formData.badge}
											onChange={handleInputChange}
											className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										/>
									</div>
								</div>

								{/* Full Detail Section */}
								<div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
									<h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
										รายละเอียดเพิ่มเติม
									</h4>

									{/* Conditions */}
									<div className="mb-4">
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											เงื่อนไข
										</label>
										{formData.fullDetail.conditions.map((condition, index) => (
											<div key={index} className="flex gap-2 mb-2">
												<input
													type="text"
													value={condition}
													onChange={(e) => handleArrayFieldChange('conditions', index, e.target.value)}
													className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
													placeholder={`เงื่อนไขที่ ${index + 1}`}
												/>
												{formData.fullDetail.conditions.length > 1 && (
													<button
														type="button"
														onClick={() => removeArrayField('conditions', index)}
														title='ลบ'
														className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
													>
														{ICON_ACTION.DELETE}
													</button>
												)}
											</div>
										))}
										<button
											type="button"
											onClick={() => addArrayField('conditions')}
											className="mt-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm"
										>
											+ เพิ่มเงื่อนไข
										</button>
									</div>

									{/* How to Join */}
									<div className="mb-4">
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											วิธีการเข้าร่วม
										</label>
										{formData.fullDetail.howToJoin.map((step, index) => (
											<div key={index} className="flex gap-2 mb-2">
												<input
													type="text"
													value={step}
													onChange={(e) => handleArrayFieldChange('howToJoin', index, e.target.value)}
													className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
													placeholder={`ขั้นตอนที่ ${index + 1}`}
												/>
												{formData.fullDetail.howToJoin.length > 1 && (
													<button
														type="button"
														onClick={() => removeArrayField('howToJoin', index)}
														title='ลบ'
														className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
													>
														{ICON_ACTION.DELETE}
													</button>
												)}
											</div>
										))}
										<button
											type="button"
											onClick={() => addArrayField('howToJoin')}
											className="mt-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm"
										>
											+ เพิ่มขั้นตอน
										</button>
									</div>

									{/* Rewards */}
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											รางวัล/ผลประโยชน์
										</label>
										<textarea
											value={formData.fullDetail.rewards}
											onChange={(e) => setFormData(prev => ({
												...prev,
												fullDetail: {
													...prev.fullDetail,
													rewards: e.target.value
												}
											}))}
											rows={3}
											className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											placeholder="รางวัลหรือผลประโยชน์ที่จะได้รับ"
										/>
									</div>
								</div>

								{/* Form Actions */}
								<div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
									<button
										type="button"
										onClick={() => setShowModal(false)}
										className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
									>
										ยกเลิก
									</button>
									<button
										type="submit"
										className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
									>
										บันทึก
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}

			{/* Custom styles for CKEditor and DatePicker in dark mode */}
			<style jsx global>{`
				.ck-editor__editable {
					min-height: 150px;
				}

				.dark .ck.ck-editor__main > .ck-editor__editable {
					background-color: #1F2937;
					color: #F9FAFB;
					border-color: #4B5563;
				}

				.dark .ck.ck-toolbar {
					background-color: #374151;
					border-color: #4B5563;
				}

				.dark .ck.ck-button:not(.ck-disabled):hover,
				.dark .ck.ck-button:not(.ck-disabled):active {
					background-color: #1F2937;
				}

				.dark .ck.ck-button .ck-button__label {
					color: #E5E7EB;
				}

				.dark .ck.ck-icon {
					color: #9CA3AF;
				}

				.dark .ck.ck-dropdown__panel {
					background-color: #374151;
					border-color: #4B5563;
				}

				.dark .ck.ck-list__item .ck-button {
					color: #E5E7EB;
				}

				.dark .ck.ck-list__item .ck-button:hover {
					background-color: #1F2937;
				}

				/* DatePicker Dark Mode Styles */
				.dark .react-datepicker {
					background-color: #1F2937;
					border-color: #4B5563;
					color: #F9FAFB;
				}

				.dark .react-datepicker__header {
					background-color: #374151;
					border-bottom-color: #4B5563;
				}

				.dark .react-datepicker__current-month,
				.dark .react-datepicker-time__header,
				.dark .react-datepicker__day-name {
					color: #F9FAFB;
				}

				.dark .react-datepicker__day,
				.dark .react-datepicker__time-list-item {
					color: #E5E7EB;
				}

				.dark .react-datepicker__day:hover,
				.dark .react-datepicker__time-list-item:hover {
					background-color: #374151;
				}

				.dark .react-datepicker__day--selected,
				.dark .react-datepicker__day--keyboard-selected,
				.dark .react-datepicker__time-list-item--selected {
					background-color: #2563EB;
					color: white;
				}

				.dark .react-datepicker__day--disabled {
					color: #6B7280;
				}

				.dark .react-datepicker__navigation-icon::before {
					border-color: #E5E7EB;
				}

				.dark .react-datepicker__time-container {
					border-left-color: #4B5563;
				}

				.dark .react-datepicker__time-list {
					background-color: #1F2937;
				}
			`}</style>
		</Layout>
	);
};

export default Manage;
